import { useState, useRef, useCallback } from 'react';
import { callApi, getPropValue } from './utils';
import './AutoComplete.css';
import useApp from '../AppContext';
import { t } from "./../i18n";

const AutoComplete = ({
  placeholderPrimary,
  placeholderSecondary,
  serverUrl,
  props,
  col,
  obj,
  disabled,
  onChange,
}) => {
  const app = useApp()
  const initialPrimaryQry = getPropValue(obj, col.columnName) || '';
  const initialSecondaryQry = getPropValue(obj, col.secondaryField) || '';
  const [query, setQuery] = useState({ primary: initialPrimaryQry, secondary: initialSecondaryQry });
  const [options, setOptions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [language] = useState(app.sessionToken?.Language);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [role] = useState(app.sessionToken?.Role);
  const fetchTimeout = useRef(null);
  const primaryInputRef = useRef(null);
  const secondaryInputRef = useRef(null);
  const containerRef = useRef(null);
  const fetchOptions = useCallback(async (searchQuery, typedValue) => {
    const allowedRoles = ['Admin', 'Planner', 'DoorUser'];
    try {
      let defaultVariables = {
        filterName: 'lookupQry',
        top: 50
      };
      const pattern = /\{([^}]*)\{([^}\s]+)\s+([^}\s]+)\s+([^}]*)\}\}/;
      const match = pattern.exec(col.lookUpQuery);
      let lkpQuery = {};
      if (match) {
        const tableName = match[1];
        const pkField = match[2];
        const lookUpField = match[3];
        const secondaryField = match[4];
        const qryStr = `{` + tableName + '(p_searchQuery:$p_searchQuery){' + pkField + ' ' + lookUpField + ' ' + secondaryField + '}}'
        lkpQuery = {
          queryStr: qryStr,
          tableName: tableName,
          pkField: pkField,
          lookUpField: lookUpField,
          secondaryField: secondaryField
        };
      }
      defaultVariables["appendFilter"] = `UPPER(${lkpQuery.lookUpField})  LIKE ?(;s;;p_searchQuery) OR UPPER(${lkpQuery.secondaryField}) LIKE ?(;s;;p_searchQuery)`;
      defaultVariables["p_searchQuery"] = `%${searchQuery.toUpperCase()}%`;
      
      const gqlResult = await callApi('POST', `${serverUrl}/api/query`, props.userToken, {
        query: lkpQuery.queryStr,
        variables: defaultVariables
      });

      // console.log('API Response:', gqlResult);
      let fetchedData = gqlResult.data[Object.keys(gqlResult.data)[0]] || [];
      // console.log('Fetched Data:', fetchedData);

      let newOptions = fetchedData.map(item => {
        const option = {
          pkField: item[lkpQuery.pkField] || '',
          lkpField: item[lkpQuery.lookUpField] || '',
          secondaryField: item[lkpQuery.secondaryField] || ''
        };
        // console.log('Mapped Option:', option);
        return option;
      });


      newOptions.sort((a, b) => {
        const aLength = a.lkpField.length;
        const bLength = b.lkpField.length;
        if (aLength !== bLength) return aLength - bLength;

        return a.lkpField.localeCompare(b.lkpField);
      });

      if (!allowedRoles.includes(role) && typedValue) {
        newOptions = newOptions.filter(opt =>
          (opt.lkpField === typedValue) ||
          (opt.secondaryField === typedValue)
        );
      }

      // console.log('Final sorted options:', newOptions);

      if (newOptions.length > 0 || searchQuery.length > 0) {
        setOptions(newOptions);
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }
    } catch (error) {
      setOptions([]);
    }
  }, [col.lookUpQuery, props.userToken, serverUrl, role]);


  const delayedFetchOptions = useCallback((searchQuery, typedValue) => {
    clearTimeout(fetchTimeout.current);
    fetchTimeout.current = setTimeout(() => {
      fetchOptions(searchQuery, typedValue);
    }, 500);
  }, [fetchOptions]);


  const handleInputChange = useCallback((event) => {
    const value = event.target.value;
    setOptions([]);
    setQuery({ primary: value, secondary: '' });

    if (value.length >= 3) {
      delayedFetchOptions(value, value);
      setIsOpen(true);
    } else {
      setIsOpen(false);
      setOptions([]);
    }

    if (!value && onChange) {
      onChange(null);
    }
  }, [delayedFetchOptions, onChange]);


  const handleFocus = useCallback(() => {
    if (role !== 'DoorUser') return;

    if (query.primary && query.primary.length >= 1) {
      setIsOpen(true);
      delayedFetchOptions(query.primary, query.primary);
    }
  }, [delayedFetchOptions, query, role]);



  const handleOptionClick = useCallback((option) => {
    setQuery({ primary: option.lkpField, secondary: option.secondaryField });
    if (onChange) {
      onChange({ option });
    }
    setIsOpen(false);
  }, [onChange]);

  const handleBlur = useCallback(() => {
    setTimeout(() => {
      if (
        document.activeElement !== primaryInputRef.current &&
        document.activeElement !== secondaryInputRef.current &&
        !containerRef.current.contains(document.activeElement)
      ) {
        setIsOpen(false);
      }
    }, 0);
  }, []);

  const handleKeyDown = useCallback((event) => {
    if (!isOpen) return;

    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex(prevIndex => (prevIndex > 0 ? prevIndex - 1 : options.length - 1));
        break;
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex(prevIndex => (prevIndex < options.length - 1 ? prevIndex + 1 : 0));
        break;
      case 'Enter':
        if (activeIndex >= 0) {
          handleOptionClick(options[activeIndex]);
        }
        break;
      default:
        break;
    }
  }, [isOpen, options, activeIndex, handleOptionClick]);

  const handleAddCustomValue = () => {
    const customValue = query.primary || query.secondary;
    if (onChange && customValue) {
      onChange({ option: { lkpField: customValue, secondaryField: '' } });
    }
    setIsOpen(false);
    setOptions([]);
  };
  return (
    <div ref={containerRef} className="AutoComplete-container" onKeyDown={handleKeyDown}>
      <div className="AutoComplete-input-container">
        <label className="AutoComplete-label">{col.caption}</label>
        <input
          type="text"
          placeholder={placeholderPrimary}
          value={query.primary}
          onChange={event => handleInputChange(event, true)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          className="form-control"
          style={{ width: '100%' }}
          size="sm"
          ref={primaryInputRef}
        />
        {/* <input
          type="text"
          placeholder={placeholderSecondary}
          value={query.secondary}
          onChange={event => handleInputChange(event, false)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          className="form-control ms-2"
          style={{ width: '70%',  }}
          size="sm"
          ref={secondaryInputRef}
        /> */}
      </div>
      {isOpen && (
        <ul className="AutoComplete-dropdown">
          <span>{t('common.choose', language)} {col.descr}
          </span>
          <div className='AutoComplete-wrapper'>
            {options.map((option, index) => (
              <li
                key={option.pkField}
                className={`AutoComplete-option ${index === activeIndex ? 'active' : ''}`}
                onMouseDown={() => handleOptionClick(option)}
              >
                <div
                  className="d-flex align-items-center justify-content-start"
                  style={{ gap: '20px', width: 'unset' }}
                >
                  <span><strong>{option.lkpField}</strong></span>
                  <span>{option.secondaryField || ''}</span>
                </div>
              </li>
            ))}
          </div>
          {options.length === 0 && query.primary.length >= 3 ? (
            <div style={{ textAlign: 'center', padding: '10px' }}>
              <p style={{ marginBottom: '10px', color: 'rgb(85 82 82)', fontSize: '13px' }}>
                {t('common.noData', language)}
              </p>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                style={{ background: "var(--color-main-background)", border: "none" }}
                onMouseDown={e => {
                  e.preventDefault();
                  handleAddCustomValue();
                }}
              >
                <i className="bi bi-plus-circle" style={{ marginRight: "5px" }}></i>{t('common.add', language)} {col.descr}
              </button>
            </div>
          ) : (
            options.length === 0 && (
              <p
                className="d-block mx-auto"
                style={{ width: 'fit-content', marginTop: '40px', color: 'rgb(85 82 82)', fontSize: '13px' }}
              >
                {t('common.noData', language)}
              </p>
            )
          )}
        </ul>
      )}
    </div>
  );
};

export default AutoComplete;
